import { KeyboardEvent, RefObject, useState } from 'react';
import { Box, InputBase } from '@mui/material';
import { ASK_MESSAGE_MAX_LENGTH } from '../types/syntiro-agent';
import {
  BORDER_ACCENT,
  BORDER_HAIRLINE,
  SKY,
  SPARK_GRADIENT,
  TEXT_MUTED_DIM,
  TEXT_PRIMARY
} from './askSyntiroTheme';

const alphaBadgeSx = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
  color: SKY,
  border: '1px solid',
  borderColor: SKY,
  borderRadius: '999px',
  px: 0.75,
  py: 0.125
};

const DEFAULT_PLACEHOLDER = 'Ask about Syntiro…';

export interface AskSyntiroComposerProps {
  onSubmit: (message: string) => void;
  disabled: boolean;
  isAwaitingReply: boolean;
  onStop: () => void;
  placeholder?: string;
  inputRef?: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
}

/**
 * Multiline composer. Clears on submit — the optimistic user turn already
 * preserves the text, so a persistent textarea would render the question
 * twice. `retryLastTurn` (wired by the panel) is the recovery path.
 */
export default function AskSyntiroComposer({
  onSubmit,
  disabled,
  isAwaitingReply,
  onStop,
  placeholder = DEFAULT_PLACEHOLDER,
  inputRef
}: AskSyntiroComposerProps) {
  const [draft, setDraft] = useState('');
  const trimmedDraft = draft.trim();
  const canSubmit = trimmedDraft.length > 0 && !disabled && !isAwaitingReply;

  const submit = (): void => {
    if (!canSubmit) return;
    onSubmit(trimmedDraft);
    setDraft('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <Box sx={{ px: 2, py: 2, borderTop: '1px solid', borderColor: BORDER_HAIRLINE }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Box component="span" sx={alphaBadgeSx}>
          alpha
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid',
          borderColor: BORDER_ACCENT,
          borderRadius: '13px',
          px: 1.5,
          py: 0.75,
          '&:focus-within': { borderColor: SKY, boxShadow: `0 0 0 3px ${BORDER_ACCENT}` }
        }}
      >
        <InputBase
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          multiline
          minRows={1}
          maxRows={6}
          fullWidth
          inputRef={inputRef}
          inputProps={{ 'aria-label': 'Ask Syntiro a question', maxLength: ASK_MESSAGE_MAX_LENGTH }}
          sx={{
            fontSize: 14.5,
            color: TEXT_PRIMARY,
            '& .MuiInputBase-input::placeholder': { color: TEXT_MUTED_DIM, opacity: 1 }
          }}
        />
        <Box
          component="button"
          type="button"
          onClick={isAwaitingReply ? onStop : submit}
          disabled={!isAwaitingReply && !canSubmit}
          aria-label={isAwaitingReply ? 'Stop generating' : 'Send'}
          title={isAwaitingReply ? 'Stop generating' : undefined}
          sx={{
            width: 38,
            height: 38,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 0,
            borderRadius: '11px',
            background: SPARK_GRADIENT,
            color: '#04282d',
            fontSize: 18,
            lineHeight: 1,
            fontWeight: 800,
            cursor: 'pointer',
            '&:disabled': { opacity: 0.5, cursor: 'default' }
          }}
        >
          {isAwaitingReply ? (
            <Box
              aria-hidden="true"
              sx={{ width: 11, height: 11, borderRadius: '2px', backgroundColor: 'currentColor' }}
            />
          ) : (
            '↑'
          )}
        </Box>
      </Box>
    </Box>
  );
}
